import React from 'react';

type BadgeVariant = 'success' | 'warning' | 'danger' | 'info' | 'neutral';
type BadgeSize = 'sm' | 'md';

export interface BadgeProps {
  variant?: BadgeVariant;
  size?: BadgeSize;
  className?: string;
  children: React.ReactNode;
  dot?: boolean;
}

const VARIANT_STYLES: Record<BadgeVariant, string> = {
  success: 'bg-emerald-400/10 text-emerald-400 border-emerald-400/20',
  warning: 'bg-amber-400/10 text-amber-400 border-amber-400/20',
  danger: 'bg-red-400/10 text-red-400 border-red-400/20',
  info: 'bg-blue-400/10 text-blue-400 border-blue-400/20',
  neutral: 'bg-surface-secondary text-content-tertiary border-subtle',
};

const DOT_COLORS: Record<BadgeVariant, string> = {
  success: 'bg-emerald-400',
  warning: 'bg-amber-400',
  danger: 'bg-red-400',
  info: 'bg-blue-400',
  neutral: 'bg-content-tertiary',
};

const SIZE_STYLES: Record<BadgeSize, string> = {
  sm: 'text-[11px] px-2 py-0.5',
  md: 'text-xs px-2.5 py-1',
};

const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  (
    {
      variant = 'neutral',
      size = 'md',
      className = '',
      children,
      dot = false,
    },
    ref,
  ) => {
    const baseClasses = [
      'inline-flex items-center gap-1 rounded-md font-medium border',
      VARIANT_STYLES[variant],
      SIZE_STYLES[size],
      className,
    ].filter(Boolean).join(' ');

    return (
      <span ref={ref} className={baseClasses}>
        {dot && (
          <span
            className={`w-1.5 h-1.5 rounded-full shrink-0 ${DOT_COLORS[variant]}`}
            aria-hidden
          />
        )}
        {children}
      </span>
    );
  },
);

Badge.displayName = 'Badge';

export default Badge;
